import React, { FC, useCallback, useEffect, useState } from 'react';
import styled, { useTheme } from 'styled-components';
import { RouteComponentProps } from 'react-router-dom';
import axios from '../../../utils/axios';
import { API_URL } from '../../../constants';
import styles from '../../../styledComponents';
import {
  ErrorMessage,
  TextField,
  Button,
  LoaderOverlay,
  Checkbox,
} from '../../../components/library';
import { AutoCloseAlert, useAutoCloseAlert } from '../../../components/library/AutoCloseAlert';
import { Alert, AlertTitle, Typography } from '@mui/material';
import { UpcSearchModal } from '../../service/UpcSearchModal';
import { useInventory } from '../../catalog/utils/useInventory';
import { Inventory, WarehouseBin } from '../../../services/inventory/types';
import { WarehouseBinAutocomplete } from '../../../components/inventory/WarehouseBinAutocomplete';
import { Box } from '@material-ui/core';
import { getZoneParentById } from '../../catalog/utils/inventory/utils';

export const SINGLE_ITEM_RELOCATOR_ROUTE = '/inventory/relocate/single-item';

const { PageWrapper } = styles;

const InlineTextField = styled(TextField)`
  display: inline-block;
`;

const CheckboxWrapper = styled.label`
  display: inline-block;
  cursor: pointer;
  min-width: 160px;
`;

interface SingleItemRelocatorProps {
  sku?: string;
  onSuccess?: () => void;
}

const SingleItemRelocator: FC<Partial<RouteComponentProps> & SingleItemRelocatorProps> = ({
  location: windowLocation,
  sku: propSku,
  onSuccess,
}) => {
  const skuParam = windowLocation ? new URLSearchParams(windowLocation.search).get('sku') : null;
  const initialSku = skuParam || propSku || '';
  const isModal = !!propSku;

  const [loading, setLoading] = useState<boolean>(false);
  const [upcModalOpen, setUpcModalOpen] = useState(false);

  const [warehouseZoneLocked, setWarehouseZoneLocked] = useState<boolean>(false);
  const [binLocked, setBinLocked] = useState<boolean>(false);
  const [inspectionStatusLocked, setInspectionStatusLocked] = useState<boolean>(false);

  const [sku, setSku] = useState<string>(initialSku);
  const [inventoryRecords, setInventoryRecords] = useState<Inventory[]>([]);
  const [fromBin, setFromBin] = useState<WarehouseBin | undefined>(undefined);
  const [toBin, setToBin] = useState<WarehouseBin | undefined>(undefined);
  const [toBinTypeahead, setToBinTypeahead] = useState<string>('');
  const [toZoneId, setToZoneId] = useState<number | undefined>(undefined);
  const [qty, setQty] = useState<number | ''>('');
  const [inspectionStatusId, setInspectionStatusId] = useState<string>('');

  const {
    autoCloseAlertOpen: successAutoCloseAlertOpen,
    handleAutoCloseAlertOpen: handleSuccessAutoCloseAlertOpen,
    handleAutoCloseAlertClose: handleSuccessAutoCloseAlertClose,
  } = useAutoCloseAlert();
  const [successMessage, setSuccessMessage] = useState<string | undefined>(undefined);
  const onSuccessAutoAlertClose = useCallback(() => {
    setSuccessMessage(undefined);
    handleSuccessAutoCloseAlertClose();
  }, [setSuccessMessage, handleSuccessAutoCloseAlertClose]);

  const {
    autoCloseAlertOpen: errorAutoCloseAlertOpen,
    handleAutoCloseAlertOpen: handleErrorAutoCloseAlertOpen,
    handleAutoCloseAlertClose: handleErrorAutoCloseAlertClose,
  } = useAutoCloseAlert();
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
  const onErrorAutoAlertClose = useCallback(() => {
    setErrorMessage(undefined);
    handleErrorAutoCloseAlertClose();
  }, [setErrorMessage, handleErrorAutoCloseAlertClose]);

  const {
    warehouseZones,
    warehouseBinsForZone,
    inspectionStatuses,
    error: inventoryHookError,
  } = useInventory({
    typeaheadVal: toBinTypeahead,
    zoneId: toZoneId,
  });

  const isRefurbished = inventoryRecords.some(r => r.isRefurbished);
  const hasInventory = inventoryRecords.length > 0;
  // A selected status only applies to refurbished items; a locked status carries to the next SKU.
  const updateInspectionStatus = isRefurbished && hasInventory && !!inspectionStatusId;

  const clearState = () => {
    setSku('');
    setInventoryRecords([]);
    setFromBin(undefined);
    setQty('');
    setLoading(false);
    setErrorMessage(undefined);
    if (!warehouseZoneLocked) setToZoneId(undefined);
    if (!binLocked) setToBin(undefined);
    if (!inspectionStatusLocked) setInspectionStatusId('');
  };

  const fetchInventoryRecords = async () => {
    if (!sku) return;
    try {
      const res = await axios.get(`${API_URL}/inventory/skus/${sku}/inventoryRecords`);
      const records: Inventory[] = res?.data ?? [];
      setInventoryRecords(records);
      // A locked status only carries between refurbished SKUs; a regular SKU resets it.
      if (!records.some(r => r.isRefurbished)) {
        setInspectionStatusId('');
        setInspectionStatusLocked(false);
      }
      if (records.length === 1) {
        setFromBin(records[0].warehouseBin);
        if (records[0].isRefurbished) setQty(1);
      } else if (records.length > 1 && records.every(r => r.isRefurbished)) {
        setQty(1);
      }
    } catch (err) {
      console.error(err?.response?.data?.message ?? err?.message ?? 'An unknown error occurred');
    }
  };

  const handleFromBinChange = (binId: number) => {
    const record = inventoryRecords.find(r => r.warehouseBinId === binId);
    setFromBin(record?.warehouseBin);
    if (!isRefurbished) setQty('');
  };

  const handleSubmit = async (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.preventDefault();
    if (!fromBin || !toZoneId || !toBin || !qty) return;

    try {
      setLoading(true);

      if (updateInspectionStatus && inspectionStatusId) {
        // Single-item endpoint — supports inspection status update
        await axios.post(`${API_URL}/inventory/skus/${sku}/relocate`, {
          toZoneId,
          toBin: toBin.code,
          toInspectionStatusId: inspectionStatusId,
        });
        const prevStatus = inventoryRecords[0]?.inspection?.status?.name;
        const nextStatus = inspectionStatuses?.find(s => s.id === parseInt(inspectionStatusId, 10))
          ?.name;
        const statusMsg =
          prevStatus && nextStatus && prevStatus !== nextStatus
            ? ` and inspection status from ${prevStatus} to ${nextStatus}`
            : '';
        setSuccessMessage(
          `Relocated ${qty}x ${sku} from ${fromBin.code} to ${toBin.code}${statusMsg}.`,
        );
      } else {
        await axios.post(`${API_URL}/inventory/skus/relocate`, {
          fromZoneId: fromBin.warehouseZoneId,
          toZoneId,
          relocationItems: [
            {
              sku,
              fromWarehouseBinId: fromBin.id,
              toWarehouseBinId: toBin.id,
              quantity: qty,
            },
          ],
        });
        setSuccessMessage(`Relocated ${qty}x ${sku} from ${fromBin.code} to ${toBin.code}.`);
      }

      handleSuccessAutoCloseAlertOpen();
      clearState();
      onSuccess?.();
    } catch (err) {
      setErrorMessage(err?.response?.data?.message ?? err?.message ?? 'An unknown error occurred');
      handleErrorAutoCloseAlertOpen();
    } finally {
      setLoading(false);
    }
  };

  const handleSkuByUpc = (value: string) => {
    setUpcModalOpen(false);
    setSku(value);
  };

  const skuInputRef = React.useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (initialSku) {
      fetchInventoryRecords();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialSku]);

  const submitDisabled =
    loading || !sku || !fromBin || !toZoneId || !toBin || !qty || !!inventoryHookError;

  const theme = useTheme();
  return (
    <PageWrapper>
      <Box
        style={{
          width: '100%',
          maxWidth: '500px',
          marginTop: isModal ? 0 : theme.spacing(8),
        }}
      >
        <LoaderOverlay loading={loading} />
        {!isModal && (
          <Typography variant="h4" gutterBottom>
            Single Item Relocator
          </Typography>
        )}
        <Box
          style={{
            border: `${theme.palette.grey[500]} 0.1em solid`,
            backgroundColor: theme.palette.grey[100],
            padding: theme.spacing(3),
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          {/* SKU / UPC */}
          <Box style={{ padding: '1em 0', alignItems: 'center', width: '100%' }}>
            <Box
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '100%',
              }}
            >
              <InlineTextField
                style={{ width: '100%' }}
                tabIndex={0}
                autoFocus
                id="SingleItemRelocateSKU"
                name="sku"
                value={sku}
                onChange={e => {
                  setSku(e.target.value);
                  setInventoryRecords([]);
                  setFromBin(undefined);
                  setQty('');
                }}
                placeholder="Enter SKU"
                label="SKU"
                variant="outlined"
                inputRef={skuInputRef}
                onBlur={fetchInventoryRecords}
              />
              <span style={{ padding: '0 1rem' }}>or</span>
              <Button onClick={() => setUpcModalOpen(true)} ordinality="secondary" tabIndex={-1}>
                Search by UPC
              </Button>
              {upcModalOpen && (
                <UpcSearchModal
                  open={upcModalOpen}
                  handleClose={() => setUpcModalOpen(false)}
                  setSku={handleSkuByUpc}
                />
              )}
            </Box>
          </Box>

          {/* FromBin */}
          <Box style={{ width: '100%', marginTop: '2rem' }}>
            <InlineTextField
              style={{ width: '100%' }}
              id="SingleItemRelocateFromBin"
              name="fromBin"
              disabled={!hasInventory || isRefurbished}
              value={fromBin?.id ?? ''}
              onChange={e => handleFromBinChange(parseInt(e.target.value, 10))}
              options={inventoryRecords.map(r => ({
                label: r.warehouseBin?.code ?? String(r.warehouseBinId),
                value: r.warehouseBinId,
              }))}
              placeholder="From Bin"
              label="From Bin"
              variant="outlined"
            />
            {fromBin && (
              <Box style={{ padding: '0 0 0 1rem' }}>
                <Typography variant="caption">
                  Qty in bin:{' '}
                  {inventoryRecords.find(r => r.warehouseBinId === fromBin.id)?.quantity ?? '—'}
                </Typography>
              </Box>
            )}
          </Box>

          {/* ToZone */}
          <Box
            style={{
              display: 'flex',
              marginTop: '2rem',
              padding: 0,
              alignItems: 'center',
              width: '100%',
            }}
          >
            <InlineTextField
              style={{ width: '100%' }}
              id="SingleItemRelocateToZone"
              name="toZoneId"
              disabled={!hasInventory}
              value={toZoneId || ''}
              onChange={e => setToZoneId(parseInt(e.target.value, 10))}
              options={warehouseZones?.map(zone => ({
                label: `${getZoneParentById(warehouseZones, zone.parentWarehouseZoneId)}${
                  zone.name
                }`,
                value: zone.id,
              }))}
              placeholder="To Zone"
              label="To Zone"
              variant="outlined"
            />
            <CheckboxWrapper>
              <Checkbox
                checked={warehouseZoneLocked}
                onChange={() => setWarehouseZoneLocked(!warehouseZoneLocked)}
                name="zoneLocked"
                tabIndex={-1}
              />
              Lock Zone
            </CheckboxWrapper>
          </Box>

          {/* ToBin */}
          <Box
            style={{
              display: 'flex',
              marginTop: '2rem',
              padding: 0,
              alignItems: 'center',
              width: '100%',
            }}
          >
            <WarehouseBinAutocomplete
              style={{ width: '100%' }}
              id="SingleItemRelocateToBin"
              selectedBin={toBin}
              options={warehouseBinsForZone}
              typeaheadVal={toBinTypeahead}
              onTypeaheadChange={setToBinTypeahead}
              onBinChange={e => {
                setToBin((e as unknown) as WarehouseBin);
                skuInputRef.current?.focus();
              }}
            />
            <CheckboxWrapper>
              <Checkbox
                checked={binLocked}
                onChange={() => setBinLocked(!binLocked)}
                tabIndex={-1}
              />
              Lock Bin
            </CheckboxWrapper>
          </Box>

          {/* Qty */}
          <Box style={{ width: '100%', marginTop: '2rem' }}>
            <InlineTextField
              style={{ width: '100%' }}
              id="SingleItemRelocateQty"
              name="qty"
              type="number"
              disabled={!hasInventory || isRefurbished}
              value={qty}
              onChange={e => setQty(e.target.value === '' ? '' : parseInt(e.target.value, 10))}
              placeholder="Quantity"
              label="Quantity"
              variant="outlined"
              inputProps={{ min: 1 }}
            />
          </Box>

          {/* Inspection status for refurbished items */}
          {isRefurbished && hasInventory && (
            <>
              <Box
                style={{
                  display: 'flex',
                  marginTop: '1rem',
                  padding: 0,
                  alignItems: 'center',
                  width: '100%',
                }}
              >
                <InlineTextField
                  style={{ width: '100%' }}
                  id="SingleItemRelocateInspectionStatus"
                  name="inspectionStatusId"
                  value={inspectionStatusId || ''}
                  onChange={e => {
                    setInspectionStatusId(e.target.value);
                  }}
                  options={inspectionStatuses?.map((s: any) => ({
                    label: s.name,
                    value: s.id,
                  }))}
                  placeholder="Inspection Status"
                  label="Inspection Status"
                  variant="outlined"
                />
                <CheckboxWrapper>
                  <Checkbox
                    checked={inspectionStatusLocked}
                    onChange={() => setInspectionStatusLocked(!inspectionStatusLocked)}
                    tabIndex={-1}
                  />
                  Lock Status
                </CheckboxWrapper>
              </Box>
              <Box style={{ width: '100%', margin: 0, padding: '0 0 0 1rem' }}>
                <Typography variant="caption">
                  Current Inspection Status: {inventoryRecords[0]?.inspection?.status?.name ?? '—'}
                </Typography>
              </Box>
            </>
          )}

          {hasInventory && !fromBin && inventoryRecords.length > 1 && (
            <Box style={{ width: '100%', marginTop: '1rem' }}>
              <Alert severity="info">This SKU is in multiple bins — select a From Bin above.</Alert>
            </Box>
          )}

          <Button
            size="large"
            type="submit"
            ordinality="primary"
            disabled={submitDisabled}
            style={{ width: '100%', marginTop: '1rem' }}
            onClick={e => handleSubmit(e)}
          >
            {updateInspectionStatus ? 'Relocate & Update Status' : 'Relocate'}
          </Button>
          <ErrorMessage error={inventoryHookError} style={{ width: '100%', marginTop: '1rem' }} />
          {successMessage && (
            <AutoCloseAlert
              open={successAutoCloseAlertOpen}
              handleClose={onSuccessAutoAlertClose}
              severity="success"
              autoHideDuration={8000}
              style={{ width: '100%', marginTop: '1rem' }}
            >
              <AlertTitle>Success</AlertTitle>
              {successMessage}
            </AutoCloseAlert>
          )}
          {errorMessage && (
            <AutoCloseAlert
              open={errorAutoCloseAlertOpen}
              handleClose={onErrorAutoAlertClose}
              autoHideDuration={15000}
              severity="error"
              style={{ width: '100%', marginTop: '1rem' }}
            >
              <AlertTitle>Error</AlertTitle>
              {errorMessage}
            </AutoCloseAlert>
          )}
        </Box>
      </Box>
    </PageWrapper>
  );
};

export default SingleItemRelocator;
